import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API_BASE, getAccessToken } from "../lib/api";
import Layout from "../components/Layout";
import BidSimulator from "../components/BidSimulator";
import AnalysisTheater from "../components/AnalysisTheater";
import EngineV4Panel from "../components/EngineV4";
import OfferDeadlineBadge from "../components/OfferDeadline";
import { FirstAnalysisUpsell } from "../components/StandardUpsell";
import {
    ArrowLeft, MapPin, Buildings, Calendar, CurrencyEur, FileText,
    Scales, MapTrifold, ChartLine, Wrench, ListChecks, FilePdf,
    CheckCircle, Clock, WarningCircle, ArrowRight, Download, Brain,
    ShieldCheck, ShieldWarning, DownloadSimple,
} from "@phosphor-icons/react";

const AGENTS = [
    { key: "document_reader", label: "Document Reader", icon: FileText },
    { key: "legal_risk", label: "Legal Risk", icon: Scales },
    { key: "urban_planning", label: "Urban Planning", icon: MapTrifold },
    { key: "market_valuation", label: "Market Valuation", icon: ChartLine },
    { key: "investment", label: "Investment Analyst", icon: CurrencyEur },
    { key: "action_planner", label: "Action Planner", icon: ListChecks },
    { key: "report", label: "Report Generator", icon: FilePdf },
];

function eur(n) {
    if (n == null || n === "") return "—";
    const v = Number(n);
    if (Number.isNaN(v)) return String(n);
    return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
}

function scoreColor(score) {
    if (score == null) return { hex: "var(--acc-muted)", bg: "bg-gray-100", text: "text-gray-700", label: "—", border: "border-gray-300" };
    if (score <= 40) return { hex: "var(--acc-red)", bg: "bg-red-50", text: "text-red-700", label: "SCARTA", border: "border-red-200" };
    if (score <= 60) return { hex: "var(--acc-orange)", bg: "bg-orange-50", text: "text-orange-700", label: "ALTO RISCHIO", border: "border-orange-200" };
    if (score <= 80) return { hex: "var(--acc-yellow)", bg: "bg-yellow-50", text: "text-yellow-800", label: "INTERESSANTE", border: "border-yellow-200" };
    return { hex: "var(--acc-green)", bg: "bg-emerald-50", text: "text-emerald-700", label: "OTTIMA", border: "border-emerald-200" };
}

const severityCls = {
    high: "bg-red-50 text-red-700 border-red-200",
    medium: "bg-orange-50 text-orange-700 border-orange-200",
    low: "bg-yellow-50 text-yellow-800 border-yellow-200",
};

export default function AnalysisDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [activeTab, setActiveTab] = useState("document_reader");
    const [downloading, setDownloading] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data: d } = await api.get(`/auctions/${id}`);
            setData(d);
        } catch (e) {
            if (e.response?.status === 404) setNotFound(true);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    // Polling while running
    useEffect(() => {
        const status = data?.analysis?.status;
        if (status !== "running" && status !== "queued") return;
        const t = setInterval(load, 3500);
        return () => clearInterval(t);
    }, [data, load]);

    const downloadReport = async () => {
        if (!data?.analysis?.id) return;
        setDownloading(true);
        try {
            const res = await fetch(`${API_BASE}/analyses/${data.analysis.id}/report.pdf`, {
                credentials: "include",
                headers: { Authorization: `Bearer ${getAccessToken()}` },
            });
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `asta-${data.auction.id.slice(0, 8)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } finally {
            setDownloading(false);
        }
    };

    const downloadDoc = async (doc) => {
        try {
            const res = await fetch(`${API_BASE}/auctions/${data.auction.id}/documents/${doc.id}/download`, {
                credentials: "include",
                headers: { Authorization: `Bearer ${getAccessToken()}` },
            });
            if (!res.ok) { return; }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = doc.original_filename || `${doc.kind || "documento"}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch { /* */ }
    };

    if (notFound) {
        return (
            <Layout>
                <div className="max-w-md mx-auto mt-16 text-center border border-gray-200 bg-white rounded-xl p-8" data-testid="analysis-not-found">
                    <div className="font-display-black text-2xl mb-2">Analisi non trovata</div>
                    <p className="text-sm text-gray-500 mb-5">
                        L'asta o l'analisi che cerchi non esiste più o non appartiene alla tua organizzazione.
                    </p>
                    <Link to="/archive" className="inline-flex h-10 px-5 items-center bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-gray-800 transition-colors" data-testid="notfound-back-btn">
                        Vai all'archivio
                    </Link>
                </div>
            </Layout>
        );
    }

    if (!data) {
        return (
            <Layout>
                <div className="text-sm text-gray-500">Caricamento…</div>
            </Layout>
        );
    }

    const { auction, documents, analysis } = data;
    const score = analysis?.investment_score;
    const sc = scoreColor(score);
    const status = analysis?.status;

    // Flip economics (with fallback to legacy analyses)
    const inv = analysis?.investment || {};
    const realisticScen = inv.scenarios?.realistic || {};
    const netProfit = inv.expected_net_profit ?? realisticScen.net_profit ?? realisticScen.net_margin;
    const roiNet = inv.roi_net_pct ?? realisticScen.roi_pct;
    const marginNet = inv.net_margin_pct ?? realisticScen.net_margin_pct;
    const recommendedSale = inv.recommended_sale_price ?? inv.resale_valuation?.realistic_sale_price;

    return (
        <Layout>
            <Link
                to="/dashboard"
                data-testid="back-to-dashboard"
                className="inline-flex items-center gap-2 text-xs text-gray-500 hover:text-black mb-4"
            >
                <ArrowLeft size={12} /> Torna alla dashboard
            </Link>

            {/* AI DISCLAIMER */}
            <div
                data-testid="ai-disclaimer-banner"
                className="flex items-start gap-2.5 border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 mb-6 text-xs leading-relaxed"
            >
                <WarningCircle size={16} weight="fill" className="shrink-0 mt-0.5 text-amber-600" />
                <span>
                    <strong>Analisi generata da intelligenza artificiale, a scopo informativo.</strong>{" "}
                    Gli output (Investment Score, ROI, offerta consigliata) sono indicativi e non
                    costituiscono consulenza legale, finanziaria o di investimento. Verifica sempre i dati
                    presso le fonti ufficiali.{" "}
                    <Link to="/disclaimer-ai" className="underline font-semibold">Leggi il Disclaimer AI</Link>.
                </span>
            </div>

            {/* HEADER */}
            <div className="bg-white border border-gray-200 mb-6 overflow-hidden" data-testid="analysis-header">
                <div className="flex flex-col md:flex-row">
                    <div className="flex-1 p-6 md:p-8">
                        <div className="overline mb-2">Analisi · {auction.province}</div>
                        <h1 className="font-display-black text-3xl sm:text-4xl leading-tight mb-3">
                            {auction.title}
                        </h1>
                        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-gray-600">
                            {auction.address && <span className="flex items-center gap-1.5"><MapPin size={12} /> {auction.address}</span>}
                            {auction.comune && <span className="flex items-center gap-1.5"><Buildings size={12} /> {auction.comune}</span>}
                            {auction.tribunale && <span className="flex items-center gap-1.5"><Scales size={12} /> {auction.tribunale}</span>}
                            {(auction.auction_date_iso || auction.auction_date) && (
                                <span className="flex items-center gap-1.5 font-mono-tight" data-testid="auction-date">
                                    <Calendar size={12} /> Asta: {auction.auction_date_iso
                                        ? auction.auction_date_iso.split("-").reverse().join("/")
                                        : auction.auction_date}
                                </span>
                            )}
                            <OfferDeadlineBadge
                                auctionDateIso={auction.auction_date_iso}
                                auctionDate={auction.auction_date}
                                testid="analysis-offer-deadline"
                            />
                        </div>
                    </div>
                    {auction.photos?.[0] && (
                        <div className="relative md:w-[300px] lg:w-[340px] min-h-[160px] md:min-h-0 shrink-0" data-testid="analysis-header-photo">
                            <img
                                src={auction.photos[0]}
                                alt=""
                                className="absolute inset-0 w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.closest("[data-testid=analysis-header-photo]").style.display = "none"; }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-[#0D1322]/70 md:from-[#0D1322] to-transparent" />
                        </div>
                    )}
                </div>
            </div>

            {/* STATUS */}
            {(status === "running" || status === "queued") && (
                <AnalysisTheater
                    stage={analysis.stage}
                    agents={analysis.agents || []}
                    previewScore={analysis.investment?.investment_score}
                />
            )}

            {status === "error" && (
                <div className="bg-red-50 border border-red-300 p-5 mb-6 fade-up" data-testid="analysis-error">
                    <div className="flex items-start gap-3 mb-3">
                        <WarningCircle size={20} weight="bold" className="text-red-700 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <div className="overline text-red-700 mb-1">Errore pipeline</div>
                            <p className="text-sm text-red-900 leading-relaxed">
                                Si è verificato un errore durante l&apos;analisi. Puoi riprovare cliccando &quot;Riavvia analisi&quot;.
                            </p>
                            {analysis.error && (
                                <details className="mt-2">
                                    <summary className="text-xs text-red-700 cursor-pointer hover:underline">Dettagli tecnici</summary>
                                    <pre className="text-[10px] mt-2 bg-white border border-red-200 p-2 overflow-x-auto font-mono-tight">{analysis.error}</pre>
                                </details>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={async () => {
                            await api.post(`/analyses/${analysis.id}/retry`);
                            load();
                        }}
                        data-testid="retry-analysis"
                        className="h-10 px-4 bg-red-700 text-white text-xs font-bold uppercase tracking-wider hover:bg-red-800"
                    >
                        Riavvia analisi
                    </button>
                </div>
            )}

            {!analysis && (
                <div className="bg-white border border-gray-200 p-8 mb-6 text-center" data-testid="no-analysis">
                    <p className="text-sm text-gray-600 mb-4">Nessuna analisi ancora avviata per questa asta.</p>
                    <button
                        onClick={async () => {
                            await api.post("/analyses/start", { auction_id: auction.id });
                            load();
                        }}
                        data-testid="start-now-button"
                        className="h-11 px-5 bg-black text-white text-sm font-semibold hover:bg-gray-800"
                    >
                        Avvia analisi
                    </button>
                </div>
            )}

            {status === "completed" && (
                <>
                    {/* PERIZIA UPLOAD HINT — sempre visibile su completed per
                        permettere upload di Avviso/Ordinanza/Documenti aggiuntivi
                        anche quando la perizia è già stata caricata. */}
                    <PerizUploadCard
                        auctionId={auction.id}
                        analysisId={analysis.id}
                        documents={documents || []}
                        discoveredDocs={data.discovered_documents || []}
                        surfaceSqm={auction.surface_sqm}
                        onUploaded={load}
                    />

                    {/* LEGACY ANALYSIS NOTICE */}
                    {inv.scenarios && !inv.decision && (
                        <div className="border-2 border-amber-300 bg-amber-50 p-5 mb-6 flex flex-col md:flex-row md:items-center gap-4" data-testid="legacy-analysis-banner">
                            <div className="flex-1">
                                <div className="overline mb-1 text-amber-700">Analisi generata con la versione precedente</div>
                                <p className="text-sm text-amber-900 leading-relaxed">
                                    Questo report non include il nuovo calcolo del <b>Profitto Netto Reale</b> (dettaglio
                                    completo di costi, imposte e decisione Buy/Reject). Rigenera l&apos;analisi per ottenerlo.
                                </p>
                            </div>
                            <button
                                onClick={async () => {
                                    await api.post("/analyses/start", { auction_id: auction.id });
                                    load();
                                }}
                                data-testid="regenerate-analysis-button"
                                className="h-11 px-5 bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 flex-shrink-0"
                            >
                                Rigenera analisi
                            </button>
                        </div>
                    )}

                    {/* AI JUDGE BANNER */}
                    {analysis.ai_judge && <AIJudgeBanner judge={analysis.ai_judge} globalConfidence={analysis.global_confidence} />}

                    {/* CONTROL ROOM GRID */}
                    {analysis.engine && <EngineV4Panel engine={analysis.engine} />}

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                        {/* SCORE */}
                        <div
                            className={`border-2 ${sc.border} bg-white p-6 lg:col-span-1 fade-up`}
                            data-testid="investment-score-card"
                        >
                            <div className="overline mb-2">Investment Score</div>
                            <div className="flex items-baseline gap-2 mb-4">
                                <div className="font-display-black text-7xl leading-none" style={{ color: sc.hex }}>
                                    {score ?? "—"}
                                </div>
                                <div className="text-2xl text-gray-300 font-mono-tight">/100</div>
                            </div>
                            <div className={`inline-block ${sc.bg} ${sc.text} px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest`}>
                                {sc.label}
                            </div>

                            {analysis.investment?.score_breakdown && (
                                <div className="mt-6 space-y-2 border-t border-gray-200 pt-4">
                                    {Object.entries(analysis.investment.score_breakdown).map(([k, v]) => (
                                        <ScoreBar key={k} label={k.replace(/_/g, " ")} value={v} />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* NET PROFIT (FLIP) */}
                        <div className="panel !border-[#10B981]/25 glow-green p-6 lg:col-span-2 fade-up relative overflow-hidden" data-testid="net-profit-card">
                            <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-[#10B981]/[0.07] blur-3xl pointer-events-none" />
                            <div className="flex items-start justify-between gap-3 mb-2">
                                <div className="overline">Utile netto (acquisto → rivendita)</div>
                                <DecisionBadge decision={inv.decision} />
                            </div>
                            <div
                                className="font-mono-tight font-bold text-5xl sm:text-6xl mb-3 tracking-tight"
                                style={{ color: netProfit != null ? (Number(netProfit) > 0 ? "var(--acc-green)" : "var(--acc-red)") : "var(--acc-muted)", textShadow: netProfit != null && Number(netProfit) > 0 ? "0 0 32px rgba(16,185,129,0.25)" : "none" }}
                                data-testid="net-profit-value"
                            >
                                {eur(netProfit)}
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed mb-4" data-testid="pocket-summary">
                                {inv.pocket_summary || inv.max_bid_rationale || "—"}
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
                                <Cell label="Prezzo max consigliato" value={eur(inv.max_recommended_bid)} />
                                <Cell label="Prezzo vendita consigliato" value={eur(recommendedSale)} />
                                <Cell label="ROI netto" value={roiNet != null ? `${roiNet}%` : "—"} />
                                <Cell label="Margine netto" value={marginNet != null ? `${marginNet}%` : "—"} />
                            </div>
                        </div>
                    </div>

                    {/* SIMULATORE DI RILANCIO */}
                    <BidSimulator inv={inv} auction={auction} />

                    {/* SCENARIOS */}
                    {analysis.investment?.scenarios && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="scenarios-section">
                            {[
                                { key: "prudent", label: "Prudente", accent: "border-blue-300", desc: "Rilanci in asta · vendita conservativa" },
                                { key: "realistic", label: "Realistico", accent: "border-gray-900", desc: "Aggiudicazione attesa · vendita di mercato" },
                                { key: "aggressive", label: "Aggressivo", accent: "border-emerald-300", desc: "Aggiudicazione al minimo · vendita ottimista" },
                            ].map(({ key, label, accent, desc }) => {
                                const sc2 = analysis.investment.scenarios[key] || {};
                                const profit = sc2.net_profit ?? sc2.net_margin;
                                const hasDetail = [sc2.purchase_costs, sc2.technical_costs, sc2.renovation_costs,
                                    sc2.holding_costs, sc2.selling_costs].some((v) => v != null);
                                return (
                                    <div
                                        key={key}
                                        className={`bg-white border-2 ${accent} p-5 fade-up`}
                                        data-testid={`scenario-${key}`}
                                    >
                                        <div className="overline mb-0.5">Scenario {label}</div>
                                        <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-2" data-testid={`scenario-desc-${key}`}>{desc}</div>
                                        <div
                                            className="font-display-black text-3xl mb-1"
                                            style={{ color: profit != null ? (Number(profit) > 0 ? "var(--acc-green)" : "var(--acc-red)") : "var(--acc-muted)" }}
                                        >
                                            {eur(profit)}
                                        </div>
                                        <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-4">
                                            Profitto netto post-tasse{sc2.roi_pct != null ? ` · ROI ${sc2.roi_pct}%` : ""}{sc2.annualized_roi_pct != null ? ` · ${sc2.annualized_roi_pct}%/anno` : ""}
                                        </div>
                                        <div className="space-y-1.5 text-xs">
                                            <KV k="Prezzo acquisto" v={eur(sc2.acquisition_price)} />
                                            {hasDetail ? (
                                                <>
                                                    <KV k="Costi acquisto" v={eur(sc2.purchase_costs)} />
                                                    <KV k="Costi tecnici" v={eur(sc2.technical_costs)} />
                                                    <KV k="Costi lavori" v={eur(sc2.renovation_costs)} />
                                                    <KV k="Costi detenzione" v={eur(sc2.holding_costs)} />
                                                    <KV k="Costi vendita" v={eur(sc2.selling_costs)} />
                                                    <KV k="di cui imposte" v={eur(sc2.total_taxes)} />
                                                    {sc2.plusvalenza_tax != null && (
                                                        <KV k="Plusvalenza (26%)" v={eur(sc2.plusvalenza_tax)} />
                                                    )}
                                                </>
                                            ) : (
                                                <KV k="Costi totali" v={eur(sc2.total_costs)} />
                                            )}
                                            <KV k="Prezzo vendita stimato" v={eur(sc2.estimated_sale_price ?? sc2.exit_value)} />
                                            <KV k="Tempi" v={sc2.holding_months ? `${sc2.holding_months} mesi` : "—"} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* NET PROFIT BREAKDOWN */}
                    <NetProfitBreakdown inv={inv} />

                    {/* DOWNLOAD REPORT */}
                    <div className="flex items-center justify-between bg-black text-white p-5 mb-6">
                        <div>
                            <div className="overline mb-1" style={{ color: "rgba(255,255,255,0.9)" }}>Report finale</div>
                            <div className="font-display-black text-lg">Scarica il PDF professionale</div>
                        </div>
                        <button
                            onClick={downloadReport}
                            disabled={downloading}
                            data-testid="download-pdf-button"
                            className="bg-white text-black h-11 px-5 text-sm font-semibold hover:bg-gray-200 flex items-center gap-2"
                        >
                            <Download size={14} weight="bold" />
                            {downloading ? "Generazione…" : "Scarica PDF"}
                        </button>
                    </div>

                    {/* TABS - AGENT OUTPUTS */}
                    <div className="bg-white border border-gray-200" data-testid="agents-tabs">
                        <div className="flex overflow-x-auto border-b border-gray-200">
                            {AGENTS.map((a) => {
                                const Icon = a.icon;
                                const active = activeTab === a.key;
                                return (
                                    <button
                                        key={a.key}
                                        data-testid={`tab-${a.key}`}
                                        onClick={() => setActiveTab(a.key)}
                                        className={`flex items-center gap-2 px-4 h-12 text-xs font-bold uppercase tracking-wider whitespace-nowrap border-r last:border-r-0 border-gray-200 ${
                                            active ? "bg-black text-white" : "bg-white text-gray-600 hover:text-black"
                                        }`}
                                    >
                                        <Icon size={14} weight="bold" />
                                        {a.label}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="p-6 md:p-8">
                            <AgentPane tab={activeTab} analysis={analysis} />
                        </div>
                    </div>

                    {/* DOCUMENTS */}
                    {documents?.length > 0 && (
                        <div className="mt-6 bg-white border border-gray-200 p-6" data-testid="documents-section">
                            <div className="overline mb-3">Documenti caricati</div>
                            <ul className="divide-y divide-gray-100">
                                {documents.map((d) => (
                                    <li key={d.id} className="flex items-center justify-between py-2 text-sm">
                                        <span className="flex items-center gap-2">
                                            <FileText size={14} />
                                            {d.original_filename}
                                            <span className="text-[10px] text-gray-500 uppercase tracking-wider">{d.kind}</span>
                                        </span>
                                        <span className="flex items-center gap-3">
                                            {d.size ? <span className="text-xs text-gray-500 font-mono-tight">{(d.size / 1024).toFixed(0)} KB</span> : null}
                                            {d.storage_path && (
                                                <button
                                                    onClick={() => downloadDoc(d)}
                                                    data-testid={`document-download-${d.id}`}
                                                    className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline"
                                                >
                                                    <DownloadSimple size={13} weight="bold" /> Scarica
                                                </button>
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* FEEDBACK WIDGET */}
                    <FeedbackWidget analysisId={analysis.id} />

                    {/* UPSELL POST PRIMA ANALISI (solo Free) */}
                    <FirstAnalysisUpsell analysisStatus={analysis.status} auctionId={auction?.id} />
                </>
            )}
        </Layout>
    );
}

function FeedbackWidget({ analysisId }) {
    const [open, setOpen] = useState(false);
    const [section, setSection] = useState("overall");
    const [rating, setRating] = useState(null);
    const [comment, setComment] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [items, setItems] = useState([]);

    useEffect(() => {
        api.get(`/validation/feedback/${analysisId}`).then(({ data }) => setItems(data)).catch(() => {});
    }, [analysisId]);

    const submit = async () => {
        if (!rating) return;
        await api.post("/validation/feedback", {
            analysis_id: analysisId, section, rating, comment,
        });
        setSubmitted(true);
        const { data } = await api.get(`/validation/feedback/${analysisId}`);
        setItems(data);
        setTimeout(() => {
            setOpen(false);
            setSubmitted(false);
            setRating(null);
            setComment("");
            setSection("overall");
        }, 1500);
    };

    return (
        <div className="mt-6 bg-white border border-gray-200 p-6" data-testid="feedback-widget">
            <div className="flex items-center justify-between">
                <div>
                    <div className="overline mb-1">Feedback (human-in-the-loop)</div>
                    <p className="text-xs text-gray-600">
                        {items.length > 0
                            ? `${items.length} feedback registrato/i — aiuta a migliorare gli agenti.`
                            : "Aiuta a calibrare gli agenti: il tuo feedback alimenta il Validation Lab."}
                    </p>
                </div>
                {!open && (
                    <button
                        data-testid="open-feedback"
                        onClick={() => setOpen(true)}
                        className="h-10 px-4 border border-gray-300 hover:border-black text-xs font-bold uppercase tracking-wider"
                    >
                        Aggiungi feedback
                    </button>
                )}
            </div>

            {open && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                    {submitted ? (
                        <div className="text-emerald-700 text-sm flex items-center gap-2">
                            <CheckCircle size={16} weight="bold" /> Grazie! Feedback registrato.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div>
                                <div className="overline mb-1.5">Sezione</div>
                                <select
                                    data-testid="feedback-section"
                                    value={section}
                                    onChange={(e) => setSection(e.target.value)}
                                    className="w-full h-10 px-3 border border-gray-300 bg-white text-sm focus:border-black focus:outline-none"
                                >
                                    <option value="overall">Valutazione complessiva</option>
                                    <option value="document_reader">Document Reader</option>
                                    <option value="legal_risk">Legal Risk</option>
                                    <option value="urban_planning">Urban Planning</option>
                                    <option value="market_valuation">Market Valuation</option>
                                    <option value="investment">Investment</option>
                                    <option value="action_planner">Action Planner</option>
                                    <option value="report">Report finale</option>
                                </select>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    data-testid="feedback-up"
                                    onClick={() => setRating("up")}
                                    className={`flex-1 h-10 border text-xs font-bold uppercase tracking-wider ${
                                        rating === "up" ? "bg-emerald-50 border-emerald-500 text-emerald-700" : "border-gray-300 hover:border-emerald-500"
                                    }`}
                                >
                                    ↑ Utile / accurato
                                </button>
                                <button
                                    data-testid="feedback-down"
                                    onClick={() => setRating("down")}
                                    className={`flex-1 h-10 border text-xs font-bold uppercase tracking-wider ${
                                        rating === "down" ? "bg-red-50 border-red-500 text-red-700" : "border-gray-300 hover:border-red-500"
                                    }`}
                                >
                                    ↓ Sbagliato / mancante
                                </button>
                            </div>
                            <textarea
                                data-testid="feedback-comment"
                                rows={3}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Cosa è mancato o sbagliato? (opzionale ma molto utile)"
                                className="w-full px-3 py-2 border border-gray-300 text-sm focus:border-black focus:outline-none"
                            />
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => { setOpen(false); setRating(null); setComment(""); }}
                                    className="h-10 px-4 border border-gray-300 text-xs font-bold uppercase tracking-wider"
                                >
                                    Annulla
                                </button>
                                <button
                                    onClick={submit}
                                    disabled={!rating}
                                    data-testid="feedback-submit"
                                    className="h-10 px-5 bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-gray-800 disabled:opacity-40"
                                >
                                    Invia feedback
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Cell({ label, value }) {
    return (
        <div className="bg-white p-3">
            <div className="overline mb-1">{label}</div>
            <div className="font-mono-tight font-bold text-lg">{value}</div>
        </div>
    );
}

function KV({ k, v }) {
    return (
        <div className="flex justify-between text-xs">
            <span className="text-gray-500">{k}</span>
            <span className="font-mono-tight font-semibold">{v}</span>
        </div>
    );
}

function ScoreBar({ label, value }) {
    const v = Math.max(0, Math.min(100, Number(value) || 0));
    return (
        <div>
            <div className="flex justify-between text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                <span>{label}</span>
                <span className="font-mono-tight text-gray-900">{v}</span>
            </div>
            <div className="w-full h-1 bg-gray-100">
                <div className="h-full bg-black" style={{ width: `${v}%` }} />
            </div>
        </div>
    );
}

function AgentPane({ tab, analysis }) {
    if (tab === "document_reader") return <DocumentReaderPane data={analysis.document_data} />;
    if (tab === "legal_risk") return <LegalPane data={analysis.legal} />;
    if (tab === "urban_planning") return <UrbanPane data={analysis.urban} />;
    if (tab === "market_valuation") return <MarketPane data={analysis.market} />;
    if (tab === "investment") return <InvestmentPane data={analysis.investment} />;
    if (tab === "action_planner") return <ActionPlanPane data={analysis.action_plan} />;
    if (tab === "report") return <ReportPane data={analysis.report} />;
    return null;
}

function DocumentReaderPane({ data }) {
    if (!data) return <p className="text-sm text-gray-500">Nessun output disponibile.</p>;
    return (
        <div data-testid="pane-document_reader">
            <ConfidenceBadge confidence={data.confidence_score} />
            <p className="text-sm text-gray-700 leading-relaxed mb-6">{data.summary || "—"}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200 border border-gray-200 mb-6">
                <Cell label="Indirizzo" value={data.address || "—"} />
                <Cell label="Comune" value={data.comune || "—"} />
                <Cell label="Tribunale" value={data.tribunale || "—"} />
                <Cell label="Procedura" value={data.procedure_number || "—"} />
                <Cell label="Data asta" value={data.auction_date || "—"} />
                <Cell label="Prezzo base" value={eur(data.base_price)} />
                <Cell label="Offerta min." value={eur(data.min_offer)} />
                <Cell label="Cauzione" value={eur(data.deposit)} />
                <Cell label="Superficie m²" value={data.surface_sqm || "—"} />
                <Cell label="Locali" value={data.rooms || "—"} />
                <Cell label="Piano" value={data.floor || "—"} />
                <Cell label="Cat. catastale" value={data.cadastral_category || "—"} />
                <Cell label="Occupazione" value={data.occupied_status || "—"} />
                <Cell label="Spese cond. arretrate" value={eur(data.condominium_fees_arrears)} />
            </div>
            {data.critical_issues?.length > 0 && (
                <div className="mb-4">
                    <div className="overline mb-2">Criticità rilevate</div>
                    <ul className="space-y-1.5">
                        {data.critical_issues.map((c, i) => (
                            <li key={i} className="text-sm flex gap-2">
                                <WarningCircle size={14} weight="bold" className="text-orange-600 mt-0.5 flex-shrink-0" />
                                {c}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function LegalPane({ data }) {
    if (!data) return <p className="text-sm text-gray-500">Nessun output disponibile.</p>;
    const score = data.legal_risk_score;
    return (
        <div data-testid="pane-legal_risk">
            <ConfidenceBadge confidence={data.confidence_score} />
            <div className="flex items-center gap-4 mb-6">
                <div className="font-display-black text-5xl">{score ?? "—"}</div>
                <div>
                    <div className="overline">Legal Risk Score</div>
                    <div className="text-xs text-gray-500">(0 = nessun rischio · 100 = estremo)</div>
                </div>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed mb-6">{data.summary}</p>
            {data.risks?.length > 0 && (
                <div className="space-y-2">
                    {data.risks.map((r, i) => (
                        <RiskRow key={i} r={r} />
                    ))}
                </div>
            )}
            {data.legal_recommendations?.length > 0 && (
                <div className="mt-6">
                    <div className="overline mb-2">Raccomandazioni legali</div>
                    <ul className="space-y-1.5">
                        {data.legal_recommendations.map((r, i) => (
                            <li key={i} className="text-sm flex gap-2">
                                <ArrowRight size={14} className="mt-0.5 flex-shrink-0" />
                                {r}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function UrbanPane({ data }) {
    if (!data) return <p className="text-sm text-gray-500">Nessun output disponibile.</p>;
    return (
        <div data-testid="pane-urban_planning">
            <ConfidenceBadge confidence={data.confidence_score} />
            <div className="flex items-center gap-4 mb-6">
                <div className="font-display-black text-5xl">{data.urban_score ?? "—"}</div>
                <div>
                    <div className="overline">Urban Risk Score</div>
                    <div className="text-xs text-gray-500">Sanatoria necessaria: <b>{data.sanatoria_needed ? "Sì" : "No"}</b></div>
                </div>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed mb-6">{data.summary}</p>
            {data.issues?.length > 0 && (
                <div className="space-y-2">
                    {data.issues.map((it, i) => (
                        <div key={i} className="border border-gray-200 p-4">
                            <div className="flex justify-between items-start gap-4 mb-2">
                                <div className="text-sm font-semibold">{it.type}</div>
                                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${severityCls[it.severity || "low"]}`}>
                                    {it.severity}
                                </span>
                            </div>
                            <p className="text-sm text-gray-700">{it.description}</p>
                            {it.estimated_cost_eur && (
                                <p className="text-xs text-gray-500 mt-2 font-mono-tight">
                                    Costo stimato: {eur(it.estimated_cost_eur)}
                                </p>
                            )}
                            {it.action_required && (
                                <p className="text-xs mt-2">→ {it.action_required}</p>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {data.estimated_regularization_cost && (
                <div className="mt-4 bg-gray-50 border border-gray-200 p-3 text-sm">
                    <b>Costo regolarizzazione totale stimato:</b>{" "}
                    <span className="font-mono-tight">{eur(data.estimated_regularization_cost)}</span>
                </div>
            )}
        </div>
    );
}

function MarketPane({ data }) {
    if (!data) return <p className="text-sm text-gray-500">Nessun output disponibile.</p>;
    return (
        <div data-testid="pane-market_valuation">
            <ConfidenceBadge confidence={data.confidence_score} />
            <p className="text-sm text-gray-700 leading-relaxed mb-6">{data.summary}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200 border border-gray-200 mb-6">
                <Cell label="Market value" value={eur(data.market_value)} />
                <Cell label="Forced sale" value={eur(data.forced_sale_value)} />
                <Cell label="After Repair (ARV)" value={eur(data.after_repair_value)} />
                <Cell label="Costo ristrutt." value={eur(data.renovation_cost_estimate)} />
                <Cell label="Canone mensile" value={eur(data.rental_value)} />
                <Cell label="Rendimento annuo" value={data.annual_rental_yield_pct ? `${data.annual_rental_yield_pct}%` : "—"} />
                <Cell label="Liquidity Score" value={data.liquidity_score ?? "—"} />
                <Cell label="Confidence Score" value={data.confidence_score ?? "—"} />
                <Cell label="€/m² applicato" value={data.applied_eur_sqm ? eur(data.applied_eur_sqm) : "—"} />
                <Cell label="OMI min-max €/m²" value={data.omi_reference ? `${data.omi_reference.min_eur_sqm ?? "—"} – ${data.omi_reference.max_eur_sqm ?? "—"}` : "—"} />
            </div>
            {data.valuation_factors?.length > 0 && (
                <div>
                    <div className="overline mb-2">Fattori utilizzati</div>
                    <div className="flex flex-wrap gap-2">
                        {data.valuation_factors.map((f, i) => (
                            <span key={i} className="text-xs px-2 py-1 border border-gray-300 bg-gray-50">{f}</span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

const DECISION_CFG = {
    STRONG_BUY: { label: "STRONG BUY", cls: "bg-emerald-600 text-white border border-emerald-600" },
    BUY: { label: "BUY", cls: "bg-emerald-50 text-emerald-800 border border-emerald-300" },
    REVIEW: { label: "REVIEW", cls: "bg-yellow-50 text-yellow-800 border border-yellow-300" },
    REJECT: { label: "REJECT", cls: "bg-red-600 text-white border border-red-600" },
};

function DecisionBadge({ decision }) {
    const cfg = DECISION_CFG[decision];
    if (!cfg) return null;
    return (
        <span
            className={`flex-shrink-0 px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${cfg.cls}`}
            data-testid="decision-badge"
        >
            {cfg.label}
        </span>
    );
}

const CONF_CFG = {
    high: { label: "Alta", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
    medium: { label: "Media", cls: "bg-yellow-50 text-yellow-800 border-yellow-300" },
    low: { label: "Bassa", cls: "bg-red-50 text-red-700 border-red-300" },
};

function BreakdownRow({ item }) {
    const conf = CONF_CFG[item.confidence];
    return (
        <div className="flex items-start justify-between gap-3 py-2 border-b border-gray-100 last:border-b-0">
            <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-gray-800">{item.label}</div>
                {(item.rationale || item.notes) && (
                    <div className="text-[11px] text-gray-500 leading-snug mt-0.5">{item.rationale || item.notes}</div>
                )}
            </div>
            {conf && (
                <span className={`flex-shrink-0 text-[9px] uppercase tracking-wider font-bold border px-1.5 py-0.5 mt-0.5 ${conf.cls}`}>
                    {conf.label}
                </span>
            )}
            <div className="flex-shrink-0 text-xs font-mono-tight font-semibold whitespace-nowrap w-20 text-right">
                {eur(item.amount_eur)}
            </div>
        </div>
    );
}

const BREAKDOWN_SECTIONS = [
    { key: "acquisto", label: "Acquisto" },
    { key: "tecnici", label: "Tecnici" },
    { key: "ristrutturazione", label: "Ristrutturazione" },
    { key: "detenzione", label: "Detenzione" },
    { key: "vendita", label: "Vendita" },
    { key: "fiscalita", label: "Fiscalità" },
];

function NetProfitBreakdown({ inv }) {
    const breakdown = inv.cost_breakdown || [];
    if (!breakdown.length) return null;
    const scen = inv.scenarios || {};
    const realistic = scen.realistic || {};
    const resale = inv.resale_valuation || {};

    const itemsOf = (key) =>
        breakdown.filter((b) => b.category === key || (key === "ristrutturazione" && b.category === "lavori"));
    const sumOf = (items) => items.reduce((a, b) => a + (Number(b.amount_eur) || 0), 0);
    const knownKeys = BREAKDOWN_SECTIONS.map((s) => s.key).concat(["lavori"]);
    const otherItems = breakdown.filter((b) => !knownKeys.includes(b.category));
    const fiscalitaTotal = sumOf(itemsOf("fiscalita"));
    const netProfit = inv.expected_net_profit ?? realistic.net_profit ?? realistic.net_margin;

    const saleMin = (scen.prudent || {}).estimated_sale_price ?? resale.value_min;
    const saleReal = realistic.estimated_sale_price ?? resale.realistic_sale_price;
    const saleMax = (scen.aggressive || {}).estimated_sale_price ?? resale.value_max;

    const WROWS = [
        { label: "Ricavo lordo di vendita", get: (s) => s.estimated_sale_price ?? s.exit_value, sign: "" },
        { label: "Prezzo di acquisto", get: (s) => s.acquisition_price, sign: "−" },
        { label: "Costi di acquisto", get: (s) => s.purchase_costs, sign: "−" },
        { label: "Costi tecnici", get: (s) => s.technical_costs, sign: "−" },
        { label: "Costi lavori", get: (s) => s.renovation_costs, sign: "−" },
        { label: "Costi di detenzione", get: (s) => s.holding_costs, sign: "−" },
        { label: "Costi di vendita", get: (s) => s.selling_costs, sign: "−" },
        { label: "Plusvalenza (26%)", get: (s) => s.plusvalenza_tax, sign: "−" },
    ];
    const SCOLS = [
        { key: "prudent", label: "Prudente" },
        { key: "realistic", label: "Realistico" },
        { key: "aggressive", label: "Aggressivo" },
    ];

    return (
        <div className="bg-white border-2 border-gray-900 mb-6 fade-up" data-testid="net-profit-breakdown">
            <div className="border-b-2 border-gray-900 px-6 py-4">
                <div className="overline mb-1">Sezione obbligatoria</div>
                <div className="font-display-black text-2xl">Net Profit Breakdown</div>
                <p className="text-xs text-gray-500 mt-1">
                    Tutte le componenti economiche dell&apos;operazione: dove si guadagna e dove si perde denaro.
                </p>
            </div>

            {/* RICAVO */}
            <div className="px-6 py-4 border-b border-gray-200" data-testid="breakdown-ricavo">
                <div className="overline mb-3">Ricavo · prezzo di vendita stimato</div>
                <div className="grid grid-cols-3 gap-px bg-gray-200 border border-gray-200">
                    <Cell label="Minimo" value={eur(saleMin)} />
                    <Cell label="Realistico" value={eur(saleReal)} />
                    <Cell label="Ottimistico" value={eur(saleMax)} />
                </div>
            </div>

            {/* COST SECTIONS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 px-6 py-4 border-b border-gray-200">
                {BREAKDOWN_SECTIONS.map(({ key, label }) => {
                    let items = itemsOf(key);
                    if (key === "acquisto" && realistic.acquisition_price != null) {
                        items = [{
                            label: "Prezzo di aggiudicazione (ipotesi realistica)",
                            amount_eur: realistic.acquisition_price,
                            confidence: "high",
                            rationale: "Prezzo di acquisto ipotizzato nello scenario realistico",
                        }, ...items];
                    }
                    if (!items.length) return null;
                    return (
                        <div key={key} className="mb-4" data-testid={`breakdown-section-${key}`}>
                            <div className="flex justify-between items-baseline border-b-2 border-gray-900 pb-1 mb-1">
                                <span className="text-[11px] font-bold uppercase tracking-widest">{label}</span>
                                <span className="text-xs font-mono-tight font-bold">{eur(sumOf(items))}</span>
                            </div>
                            {items.map((b, i) => <BreakdownRow key={i} item={b} />)}
                        </div>
                    );
                })}
                {otherItems.length > 0 && (
                    <div className="mb-4">
                        <div className="flex justify-between items-baseline border-b-2 border-gray-900 pb-1 mb-1">
                            <span className="text-[11px] font-bold uppercase tracking-widest">Altro</span>
                            <span className="text-xs font-mono-tight font-bold">{eur(sumOf(otherItems))}</span>
                        </div>
                        {otherItems.map((b, i) => <BreakdownRow key={i} item={b} />)}
                    </div>
                )}
            </div>

            {/* WATERFALL PER SCENARIO */}
            <div className="px-6 py-4" data-testid="breakdown-waterfall">
                <div className="overline mb-3">Dal ricavo lordo al profitto netto</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="bg-gray-900 text-white">
                                <th className="text-left font-bold uppercase tracking-wider px-3 py-2">Voce</th>
                                {SCOLS.map((c) => (
                                    <th key={c.key} className="text-right font-bold uppercase tracking-wider px-3 py-2">{c.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {WROWS.map((row, ri) => (
                                <tr key={ri} className="border-b border-gray-100">
                                    <td className="px-3 py-1.5 text-gray-700">{row.sign && <span className="text-gray-400 mr-1">{row.sign}</span>}{row.label}</td>
                                    {SCOLS.map((c) => (
                                        <td key={c.key} className="px-3 py-1.5 text-right font-mono-tight">{eur(row.get(scen[c.key] || {}))}</td>
                                    ))}
                                </tr>
                            ))}
                            <tr className="border-b border-gray-100 text-gray-500">
                                <td className="px-3 py-1.5 italic">di cui imposte incluse</td>
                                {SCOLS.map((c) => (
                                    <td key={c.key} className="px-3 py-1.5 text-right font-mono-tight italic">{eur((scen[c.key] || {}).total_taxes)}</td>
                                ))}
                            </tr>
                            <tr className="bg-[#10B981]/[0.08] border-t-2 border-[#10B981]/40">
                                <td className="px-3 py-2.5 font-bold uppercase tracking-wider text-emerald-300">= Utile netto</td>
                                {SCOLS.map((c) => {
                                    const p = (scen[c.key] || {}).net_profit ?? (scen[c.key] || {}).net_margin;
                                    return (
                                        <td key={c.key} className="px-3 py-2.5 text-right font-mono-tight font-bold text-base"
                                            style={{ color: p != null ? (Number(p) > 0 ? "var(--acc-green)" : "var(--acc-red)") : "var(--acc-muted)" }}>
                                            {eur(p)}
                                        </td>
                                    );
                                })}
                            </tr>
                        </tbody>
                    </table>
                </div>
                {realistic.plusvalenza_tax != null ? (
                    <div className="mt-3 bg-gray-50 border border-gray-200 p-3 text-xs flex flex-wrap justify-between gap-2" data-testid="post-tax-line">
                        <span>Plusvalenza (26%) già sottratta dal profitto netto: <b className="font-mono-tight">−{eur(realistic.plusvalenza_tax)}</b> (scenario realistico)</span>
                        <span className="text-gray-500">Regime: persona fisica, rivendita entro 5 anni · con società il regime cambia (IRES)</span>
                    </div>
                ) : (fiscalitaTotal > 0 && netProfit != null && (
                    <div className="mt-3 bg-gray-50 border border-gray-200 p-3 text-xs flex flex-wrap justify-between gap-2" data-testid="post-tax-line">
                        <span>Tassazione stimata post-operazione (fiscalità): <b className="font-mono-tight">−{eur(fiscalitaTotal)}</b></span>
                        <span>Profitto netto post-tassazione (realistico): <b className="font-mono-tight">{eur(netProfit - fiscalitaTotal)}</b></span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function InvestmentPane({ data }) {
    if (!data) return <p className="text-sm text-gray-500">Nessun output disponibile.</p>;
    const resale = data.resale_valuation || {};
    return (
        <div data-testid="pane-investment">
            <ConfidenceBadge confidence={data.confidence_score} />
            <p className="text-sm text-gray-700 leading-relaxed mb-6">{data.summary}</p>

            {/* DECISION */}
            {data.decision && (
                <div className="border-2 border-gray-900 p-4 mb-4 flex items-start gap-4" data-testid="investment-decision">
                    <DecisionBadge decision={data.decision} />
                    <p className="text-sm text-gray-700 leading-relaxed">{data.decision_rationale || "—"}</p>
                </div>
            )}

            {/* RESALE VALUATION */}
            {(resale.value_realistic != null || resale.realistic_sale_price != null) && (
                <div className="border border-gray-200 p-4 mb-4" data-testid="resale-valuation">
                    <div className="overline mb-3">Valutazione di rivendita</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 border border-gray-200 mb-3">
                        <Cell label="Valore minimo" value={eur(resale.value_min)} />
                        <Cell label="Valore realistico" value={eur(resale.value_realistic)} />
                        <Cell label="Valore massimo" value={eur(resale.value_max)} />
                        <Cell label="Prezzo vendita realistico" value={eur(resale.realistic_sale_price)} />
                    </div>
                    {resale.expected_sale_months != null && (
                        <p className="text-xs text-gray-600 mb-2">
                            Tempi di vendita stimati: <b>{resale.expected_sale_months} mesi</b>
                        </p>
                    )}
                    {resale.market_evidence?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {resale.market_evidence.map((ev, i) => (
                                <span key={i} className="text-xs px-2 py-1 border border-gray-300 bg-gray-50">{ev}</span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* SCORE EXPLAINABILITY */}
            {data.score_explainability?.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 p-4 mb-4" data-testid="score-explainability">
                    <div className="flex items-center gap-2 mb-2">
                        <Brain size={14} weight="bold" />
                        <div className="overline">Spiegazione del punteggio</div>
                    </div>
                    <ul className="space-y-1.5">
                        {data.score_explainability.map((ex, i) => (
                            <li key={i} className="text-xs text-gray-700 flex gap-2">
                                <span className="text-gray-400 font-mono-tight">{String(i + 1).padStart(2, "0")}</span>
                                {ex}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {data.rental_strategy && (
                <div className="border border-gray-200 p-4 mb-4">
                    <div className="overline mb-2">Opzione alternativa · Locazione</div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                        <div><span className="text-gray-500">Canone:</span><br /><b className="font-mono-tight">{eur(data.rental_strategy.monthly_rent)}/mese</b></div>
                        <div><span className="text-gray-500">Rendimento:</span><br /><b className="font-mono-tight">{data.rental_strategy.annual_yield_pct}%</b></div>
                        <div><span className="text-gray-500">Payback:</span><br /><b className="font-mono-tight">{data.rental_strategy.payback_years} anni</b></div>
                    </div>
                    {data.rental_strategy.notes && <p className="text-xs text-gray-600 mt-3">{data.rental_strategy.notes}</p>}
                </div>
            )}
        </div>
    );
}

function ConfidenceBadge({ confidence }) {
    if (confidence == null) return null;
    const c = Number(confidence);
    const cls = c >= 70 ? "border-emerald-300 bg-emerald-50 text-emerald-700"
              : c >= 40 ? "border-yellow-300 bg-yellow-50 text-yellow-800"
              : "border-red-300 bg-red-50 text-red-700";
    return (
        <div className={`inline-flex items-center gap-1.5 border ${cls} text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 mb-3`} data-testid="confidence-badge">
            Confidence
            <span className="font-mono-tight">{c}/100</span>
        </div>
    );
}

function AIJudgeBanner({ judge, globalConfidence }) {
    const violation = judge?._schema_violation;
    const verdict = (judge?.judge_verdict || "").trim();
    // Fallback for malformed judge response
    if (violation || !verdict) {
        return (
            <div className="border-2 border-gray-300 bg-gray-50 text-gray-800 p-5 mb-6 fade-up" data-testid="ai-judge-banner">
                <div className="flex items-start gap-4">
                    <Brain size={24} weight="bold" className="flex-shrink-0 mt-1 text-gray-400" />
                    <div className="flex-1">
                        <div className="overline mb-1.5">AI Judge · Output non disponibile</div>
                        <p className="text-sm text-gray-600">
                            La verifica AI Judge non è stata completata correttamente.
                            Affidabilità complessiva calcolata sulla media delle confidenze per-agente.
                        </p>
                        {globalConfidence != null && (
                            <div className="mt-3 inline-block bg-white border border-gray-300 p-2">
                                <div className="overline mb-0.5">Global Confidence</div>
                                <div className="font-mono-tight font-bold">{globalConfidence}/100</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
    const cls = {
        TRUSTWORTHY: "bg-emerald-50 border-emerald-300 text-emerald-800",
        USE_WITH_CAUTION: "bg-yellow-50 border-yellow-300 text-yellow-800",
        RECONSIDER: "bg-orange-50 border-orange-300 text-orange-800",
        UNRELIABLE: "bg-red-50 border-red-300 text-red-800",
    }[verdict] || "bg-gray-50 border-gray-300 text-gray-800";
    const Icon = verdict === "TRUSTWORTHY" ? ShieldCheck : verdict === "UNRELIABLE" ? ShieldWarning : Brain;
    return (
        <div className={`border-2 ${cls} p-5 mb-6 fade-up`} data-testid="ai-judge-banner">
            <div className="flex items-start gap-4">
                <Icon size={28} weight="bold" className="flex-shrink-0 mt-1" />
                <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1.5">
                        <div className="overline">AI Judge · Independent Review</div>
                        <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 bg-white border border-current">
                            {verdict.replace(/_/g, " ")}
                        </span>
                    </div>
                    <p className="text-sm leading-relaxed mb-3">{judge.judge_summary}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <MetricChip label="Consistency" value={judge.consistency_score} />
                        <MetricChip label="Plausibility" value={judge.plausibility_score} />
                        <MetricChip label="Completeness" value={judge.completeness_score} />
                        <MetricChip label="Global confidence" value={globalConfidence ?? judge.overall_confidence} highlighted />
                    </div>
                    {judge.flags?.length > 0 && (
                        <details className="mt-3">
                            <summary className="text-xs cursor-pointer hover:underline">{judge.flags.length} flag rilevati</summary>
                            <ul className="mt-2 space-y-1">
                                {judge.flags.map((f, i) => (
                                    <li key={i} className="text-[11px] flex gap-2">
                                        <span className="font-bold uppercase tracking-wider">[{f.severity}] {f.section}:</span>
                                        <span>{f.message}</span>
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </div>
            </div>
        </div>
    );
}

function MetricChip({ label, value, highlighted = false }) {
    return (
        <div className={`bg-white border ${highlighted ? "border-black" : "border-current/30"} p-2`}>
            <div className="overline mb-0.5">{label}</div>
            <div className={`font-mono-tight font-bold ${highlighted ? "text-base" : "text-sm"}`}>{value ?? "—"}/100</div>
        </div>
    );
}

function ActionPlanPane({ data }) {
    if (!data) return <p className="text-sm text-gray-500">Nessun output disponibile.</p>;
    const phases = [
        { k: "before_auction", label: "1 · Prima dell'asta", icon: Clock },
        { k: "after_award", label: "2 · Dopo aggiudicazione", icon: CheckCircle },
        { k: "post_renovation", label: "3 · Post ristrutturazione", icon: Wrench },
        { k: "sale_or_rental", label: "4 · Vendita o locazione", icon: ArrowRight },
    ];
    return (
        <div data-testid="pane-action_planner">
            <p className="text-sm text-gray-700 mb-2">{data.summary}</p>
            {data.estimated_total_timeline_months && (
                <div className="text-xs text-gray-500 mb-6">
                    Timeline totale stimata: <b className="font-mono-tight">{data.estimated_total_timeline_months} mesi</b>
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {phases.map(({ k, label, icon: Icon }) => (
                    <div key={k} className="border border-gray-200 p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Icon size={14} weight="bold" />
                            <div className="overline">{label}</div>
                        </div>
                        <ul className="space-y-2">
                            {(data[k] || []).map((a, i) => (
                                <li key={i} className="text-sm flex gap-2">
                                    <span className="text-gray-400 font-mono-tight text-[10px] mt-1">{String(i + 1).padStart(2, "0")}</span>
                                    {a}
                                </li>
                            ))}
                            {(!data[k] || data[k].length === 0) && (
                                <li className="text-xs text-gray-400">Nessuna azione</li>
                            )}
                        </ul>
                    </div>
                ))}
            </div>
            {data.key_professionals_needed?.length > 0 && (
                <div className="mt-5">
                    <div className="overline mb-2">Professionisti necessari</div>
                    <div className="flex flex-wrap gap-2">
                        {data.key_professionals_needed.map((p, i) => (
                            <span key={i} className="text-xs px-2.5 py-1 bg-black text-white uppercase tracking-wider font-bold">
                                {p}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function ReportPane({ data }) {
    if (!data) return <p className="text-sm text-gray-500">Nessun output disponibile.</p>;
    const decision = data.decision_recommendation || "—";
    const decisionCls = {
        PROCEDERE: "bg-emerald-50 text-emerald-700 border-emerald-200",
        VALUTARE_CON_CAUTELA: "bg-yellow-50 text-yellow-800 border-yellow-200",
        APPROFONDIRE: "bg-blue-50 text-blue-700 border-blue-200",
        SCARTARE: "bg-red-50 text-red-700 border-red-200",
    }[decision] || "border-gray-200";
    return (
        <div data-testid="pane-report">
            <ConfidenceBadge confidence={data.confidence_score} />
            <div className={`border ${decisionCls} p-4 mb-6`}>
                <div className="overline mb-1">Raccomandazione</div>
                <div className="font-display-black text-2xl tracking-tight">{decision.replace(/_/g, " ")}</div>
            </div>
            <div className="mb-6">
                <div className="overline mb-2">Executive Summary</div>
                <p className="text-sm text-gray-700 leading-relaxed">{data.executive_summary}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="border border-emerald-200 bg-emerald-500/[0.07] p-4">
                    <div className="overline text-emerald-700 mb-2">Punti di forza</div>
                    <ul className="space-y-1.5">
                        {(data.key_strengths || []).map((s, i) => (
                            <li key={i} className="text-sm flex gap-2"><CheckCircle size={14} weight="bold" className="text-emerald-600 mt-0.5 flex-shrink-0" />{s}</li>
                        ))}
                    </ul>
                </div>
                <div className="border border-red-200 bg-red-500/[0.07] p-4">
                    <div className="overline text-red-700 mb-2">Criticità chiave</div>
                    <ul className="space-y-1.5">
                        {(data.key_concerns || []).map((s, i) => (
                            <li key={i} className="text-sm flex gap-2"><WarningCircle size={14} weight="bold" className="text-red-600 mt-0.5 flex-shrink-0" />{s}</li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="mb-4">
                <div className="overline mb-2">Conclusioni</div>
                <p className="text-sm text-gray-700 leading-relaxed">{data.conclusions}</p>
            </div>
            {data.disclaimer && (
                <p className="text-[10px] text-gray-500 mt-6 border-t border-gray-200 pt-3">{data.disclaimer}</p>
            )}
        </div>
    );
}

function RiskRow({ r }) {
    return (
        <div className="border border-gray-200 p-3 flex flex-col md:flex-row md:items-center gap-2">
            <div className="flex items-center gap-2 md:w-48">
                <span className="text-xs font-bold uppercase tracking-wider">{r.category}</span>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${severityCls[r.severity || "low"]}`}>
                    {r.severity}
                </span>
            </div>
            <div className="text-sm text-gray-700 flex-1">{r.description}</div>
            {r.mitigation && <div className="text-xs text-gray-500 md:max-w-xs">→ {r.mitigation}</div>}
        </div>
    );
}

const DOC_KINDS = [
    { key: "perizia",   label: "Perizia tecnica",      hint: "Relazione di stima del CTU" },
    { key: "avviso",    label: "Avviso di vendita",    hint: "Condizioni e date dell'asta" },
    { key: "ordinanza", label: "Ordinanza",            hint: "Decreto di delega / vendita" },
    { key: "other",     label: "Documento con foto",   hint: "Planimetrie, foto, allegati" },
];

const ACQ_REASON_LABELS = {
    robots_disallow: "Bloccato dal portale (robots.txt) — richiede caricamento manuale",
    not_pdf: "Il link non è un PDF pubblico (probabile pagina con login) — richiede caricamento manuale",
    too_large: "File troppo grande per il download automatico — caricalo manualmente",
    junk_document: "Documento non rilevante (modulistica del portale)",
    connect_timeout: "Sito non raggiungibile — riprova con «Rigenera analisi»",
    network_error: "Errore di rete — riprova con «Rigenera analisi»",
    dns_error: "Sito non raggiungibile",
};

function acqReasonLabel(reason) {
    if (!reason) return "Non ancora scaricato — verrà tentato alla prossima analisi";
    if (ACQ_REASON_LABELS[reason]) return ACQ_REASON_LABELS[reason];
    if (reason.startsWith("status_")) return `Errore dal sito di origine (HTTP ${reason.slice(7)}) — caricalo manualmente`;
    return "Download non riuscito — caricalo manualmente";
}

function DiscoveredDocsPanel({ discoveredDocs }) {
    if (!discoveredDocs.length) return null;
    const hasBlocked = discoveredDocs.some((d) => d.status !== "imported");
    const kindLabelOf = (k) => DOC_KINDS.find((x) => x.key === k)?.label || (k === "other" ? "Documento" : k);
    return (
        <div className="mt-4 bg-white border border-blue-200 p-4" data-testid="discovered-docs-panel">
            <div className="overline text-blue-800 mb-2">
                Documenti trovati nell&apos;asta ({discoveredDocs.length})
            </div>
            <ul className="space-y-1.5">
                {discoveredDocs.map((d, i) => (
                    <li key={i} data-testid={`discovered-doc-${i}`} className="flex items-start gap-2 text-xs">
                        {d.status === "imported"
                            ? <CheckCircle size={14} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                            : <WarningCircle size={14} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />}
                        <div className="flex-1">
                            <span className="font-bold text-blue-900">{kindLabelOf(d.kind)}</span>
                            <span className="text-gray-500 ml-2">{d.name || d.host}</span>
                            <div className={`mt-0.5 ${d.status === "imported" ? "text-emerald-700" : "text-amber-700"}`}>
                                {d.status === "imported"
                                    ? "Importato automaticamente e usato nell'analisi"
                                    : acqReasonLabel(d.reason)}
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
            {hasBlocked && (
                <p className="text-xs text-blue-900 mt-3 border-t border-blue-100 pt-2" data-testid="blocked-docs-instruction">
                    I documenti segnati in giallo non sono scaricabili automaticamente (il portale lo vieta
                    o il link non è un PDF pubblico).{" "}
                    <b>Scaricali dal sito dell&apos;asta nel tuo browser e caricali qui sotto: verranno usati
                    subito nell&apos;analisi.</b>
                </p>
            )}
        </div>
    );
}

function PerizUploadCard({ auctionId, analysisId, documents = [], discoveredDocs = [], surfaceSqm, onUploaded }) {
    const [kind, setKind] = useState("perizia");
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [uploaded, setUploaded] = useState([]); // [{kind, filename, ...result}]
    const [err, setErr] = useState(null);

    // Documents already attached to the auction (from previous upload or pipeline)
    const existingKinds = new Set(documents.map((d) => d.kind));
    // Plus newly uploaded in this session
    uploaded.forEach((u) => existingKinds.add(u.kind));

    const missingPerizia = !existingKinds.has("perizia");
    const isIncomplete = missingPerizia || documents.length === 0;

    const upload = async () => {
        if (!file) return;
        setBusy(true); setErr(null);
        try {
            const fd = new FormData();
            fd.append("kind", kind);
            fd.append("file", file);
            const { data: res } = await api.post(
                `/auctions/${auctionId}/documents`, fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            setUploaded((u) => [...u, { ...res, kind, filename: file.name }]);
            setFile(null);
            const input = document.querySelector('[data-testid="perizia-upload-input"]');
            if (input) input.value = "";
        } catch (e) {
            setErr(e.response?.data?.detail || "Upload fallito");
        } finally { setBusy(false); }
    };

    const regen = async () => {
        setBusy(true);
        try {
            await api.post(`/analyses/${analysisId}/retry`);
            onUploaded?.();
        } finally { setBusy(false); }
    };

    const hasNewPerizia = uploaded.some((u) => u.kind === "perizia");
    const kindLabel = DOC_KINDS.find((k) => k.key === kind)?.label || kind;

    // Adapt the banner copy to the state
    const headline = isIncomplete
        ? "Documenti dell'asta mancanti o incompleti"
        : "Aggiungi altri documenti per arricchire l'analisi";
    const subtext = isIncomplete
        ? "L'asta non ha documenti leggibili (o il sito di origine — es. documents.astalegale.net — blocca lo scaricamento automatico tramite robots.txt). Scarica i PDF dal sito originale nel tuo browser e caricali qui sotto uno alla volta: faccio OCR locale, estraggo la superficie dalla perizia e potrai rigenerare l'analisi con dati completi."
        : "Hai già la perizia (e la superficie). Per migliorare ulteriormente la valutazione (riduzione del rischio legale, conferma occupazione, condizione attuale) puoi caricare anche Avviso di Vendita, Ordinanza e Documento con foto. Dopo l'upload puoi rigenerare l'analisi per integrare i nuovi documenti.";

    return (
        <div
            data-testid="perizia-upload-card"
            className="border-2 border-blue-300 bg-blue-50 p-5 mb-6"
        >
            <div className="flex items-start gap-3 mb-3">
                <FilePdf size={20} weight="bold" className="text-blue-700 shrink-0 mt-0.5" />
                <div className="flex-1">
                    <div className="overline text-blue-700 mb-1">{headline}</div>
                    <p className="text-sm text-blue-900 leading-relaxed">{subtext}</p>
                </div>
            </div>

            <DiscoveredDocsPanel discoveredDocs={discoveredDocs} />

            {/* Kind selector */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 mb-3">
                {DOC_KINDS.map((k) => {
                    const isActive = kind === k.key;
                    const isDone = existingKinds.has(k.key);
                    return (
                        <button
                            key={k.key}
                            type="button"
                            onClick={() => { setKind(k.key); setErr(null); }}
                            data-testid={`perizia-upload-kind-${k.key}`}
                            className={`text-left p-3 border transition-colors ${
                                isActive
                                    ? "border-blue-700 bg-white"
                                    : "border-blue-200 bg-white/60 hover:border-blue-400"
                            }`}
                        >
                            <div className="flex items-center gap-1.5">
                                <span className={`text-xs font-bold uppercase tracking-wider ${isActive ? "text-blue-800" : "text-blue-700"}`}>
                                    {k.label}
                                </span>
                                {isDone && (
                                    <CheckCircle size={12} weight="fill" className="text-emerald-600" />
                                )}
                            </div>
                            <div className="text-[11px] text-gray-500 mt-0.5">{k.hint}</div>
                        </button>
                    );
                })}
            </div>

            {/* File picker for selected kind */}
            <div className="flex flex-col sm:flex-row gap-2">
                <label
                    className="flex-1 flex items-center gap-2 h-11 px-4 border border-dashed border-blue-400 bg-white cursor-pointer hover:border-blue-700 text-sm text-blue-800"
                    data-testid="perizia-upload-label"
                >
                    <Download size={14} weight="bold" />
                    {file ? file.name : `Seleziona PDF · ${kindLabel}`}
                    <input
                        type="file"
                        accept="application/pdf"
                        data-testid="perizia-upload-input"
                        className="hidden"
                        onChange={(e) => { setFile(e.target.files?.[0] || null); setErr(null); }}
                    />
                </label>
                <button
                    onClick={upload}
                    disabled={!file || busy}
                    data-testid="perizia-upload-submit"
                    className="h-11 px-5 bg-blue-700 text-white text-sm font-semibold hover:bg-blue-800 disabled:opacity-50"
                >
                    {busy ? "Caricamento…" : "Carica"}
                </button>
            </div>

            {err && (
                <div className="mt-3 text-xs text-red-700" data-testid="perizia-upload-error">
                    {typeof err === "string" ? err : JSON.stringify(err)}
                </div>
            )}

            {/* Uploaded files list (current session only) */}
            {uploaded.length > 0 && (
                <div
                    className="mt-4 bg-white border border-blue-200 p-4 space-y-2"
                    data-testid="perizia-upload-result"
                >
                    <div className="overline text-blue-800 mb-2">Documenti caricati ({uploaded.length})</div>
                    <ul className="space-y-1.5">
                        {uploaded.map((u, i) => {
                            const klabel = DOC_KINDS.find((k) => k.key === u.kind)?.label || u.kind;
                            return (
                                <li
                                    key={i}
                                    data-testid={`perizia-uploaded-${u.kind}-${i}`}
                                    className="flex items-start gap-2 text-xs"
                                >
                                    <CheckCircle size={14} weight="bold" className="text-emerald-600 mt-0.5 shrink-0" />
                                    <div className="flex-1">
                                        <div>
                                            <span className="font-bold text-blue-900">{klabel}</span>
                                            <span className="text-gray-500 ml-2">{u.filename}</span>
                                        </div>
                                        <div className="text-gray-600 mt-0.5">
                                            {u.extracted_text_length} caratteri{u.ocr_used && ` · OCR ${u.ocr_pages} pp.`}
                                            {u.surface_extracted && (
                                                <span className="text-emerald-700 font-semibold ml-2">
                                                    Superficie: {u.surface_extracted} m²
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>

                    <button
                        onClick={regen}
                        disabled={busy || (!hasNewPerizia && missingPerizia)}
                        data-testid="perizia-regenerate-analysis"
                        title={(!hasNewPerizia && missingPerizia) ? "Carica almeno la perizia per rigenerare l'analisi" : undefined}
                        className="mt-3 h-10 px-4 bg-blue-700 text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-800 disabled:opacity-50"
                    >
                        {busy ? "Avvio…" : "Rigenera analisi con i nuovi documenti"}
                    </button>
                </div>
            )}
        </div>
    );
}

